import {
  Box,
  Checkbox,
  Divider,
  Button as ECButton,
  Spacer,
} from '@eatclub-apps/ec-component-library';
import { useTheme } from '@material-ui/core';
import { Editor, EditorState, convertFromRaw } from 'draft-js';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ReactComponent as AmexSVG } from '../../../assets/icons/amex_icon_large.svg';
import { ReactComponent as MasterCardSVG } from '../../../assets/icons/mastercard_icon.svg';
import { ReactComponent as VisaSVG } from '../../../assets/icons/visa_icon.svg';
import { Button } from '../../../components/Button';
import { Typography } from '../../../components/Typography';
import {
  batchUpdateBookingAction,
  removeAddonAction,
  updateBookingAction,
  updateBookingStepAction,
} from '../../../data/actions/bookingAction';
import { fetchBookingTermsAction } from '../../../data/actions/bookingTermsAction';
import { updateHeldExperienceAction } from '../../../data/actions/experienceAction';
import { addMessageAction } from '../../../data/actions/messagesAction';
import {
  bookingUpdateCheckAction,
  createObeeBookingAction,
  updateObeeBookingAction,
} from '../../../data/actions/obeeBookingAction';
import {
  fetchPaymentSettingsAction,
  fetchPaymentSettingsByIdAction,
} from '../../../data/actions/paymentSettingsAction';
import {
  bookingKeys,
  bookingPropTypes,
  bookingSteps,
  isEditing,
} from '../../../data/models/Booking';
import { bookingTermsPropTypes } from '../../../data/models/BookingTerms';
import { dietaryTagsPropTypes } from '../../../data/models/DietaryTag';
import { addonGroupsPropTypes, experiencesPropTypes } from '../../../data/models/Experience';
import { paymentSettingsPropTypes } from '../../../data/models/PaymentSettings';
import { restaurantPropTypes } from '../../../data/models/Restaurant';
import { obeeBookingPropTypes } from '../../../data/models/obeeBooking';
import { PageLayout } from '../../../layouts/PageLayout';
import { WidgetLayout } from '../../../layouts/WidgetLayout';
import { trackCompletion } from '../../../utils/analytics';
import {
  getAddonGroupsForBooking,
  getExperienceTerms,
  getVouchersWithTotals,
} from '../../../utils/bookingHelpers';
import { isObee } from '../../../utils/constants';
import { devLog } from '../../../utils/devLog';
import {
  deepEqual,
  dollarsToCents,
  emptyToNull,
  findObjectByProperty,
  formatCurrency,
  formatDateToMoment,
  getBookingDuration,
  isEmpty,
  minutesToTimeString,
  safeDivision,
  sortByProperty,
} from '../../../utils/helpers';
import { useIsMobile } from '../../../utils/hooks';
import { safeParse } from '../../../utils/stringHelpers';
import { getLabelForValue } from '../../../utils/valueLists';
import CardFields from './CardFields/CardFields';
import PaymentPolicyModal from './PaymentPolicyModal';
import useStyles from './ReviewStyles';
import VoucherReferences from './VoucherReference/VoucherReferences';

const Review = ({
  addMessage,
  batchUpdateBooking,
  booking, // The local booking being created/edited
  bookingTerms,
  createObeeBooking,
  obeeBooking, // The booking fetched from the API when editing
  restaurant,
  updateBookingStep,
  updateObeeBooking,
  paymentSettings,
  fetchPaymentSettings,
  fetchPaymentSettingsById,
  fetchBookingTerms,
  experiences,
  addonGroups,
  updateHeldExperience,
  dietaryTags,
  action,
  onKillTimer,
}) => {
  const theme = useTheme();
  const classes = useStyles();

  // eslint-disable-next-line no-unused-vars
  const [cardName, _setCardName] = useState('');
  const [confirmingBooking, setConfirmingBooking] = useState(false);

  const paymentRef = useRef(null); // So each payment provider can handle their own validation

  const canSubscribe = restaurant.data?.showMarketingOptIn;
  const [subscribe, setSubscribe] = useState(false);
  const [showPaymentPolicyModal, setShowPaymentPolicyModal] = useState(false);

  const isMobile = useIsMobile();

  const dateFormatted = formatDateToMoment(booking.date).format('dddd, Do of MMMM');
  const timeFormatted = minutesToTimeString(booking.availabilitySuggestion?.time);

  const waitlist = booking?.waitlist;

  const waitlistCCRequired = safeParse(restaurant?.data?.bookingWidgetSettings)?.widgetSettings
    ?.settingsDetails?.waitlistCCRequired;

  const waitlistCCShortDesc = safeParse(restaurant?.data?.bookingWidgetSettings)?.widgetSettings
    ?.settingsDetails?.waitlistCCShortDesc;

  const waitlistCCTermsAndConditions = safeParse(restaurant?.data?.bookingWidgetSettings)
    ?.widgetSettings?.settingsDetails?.waitlistCCTermsAndConditions;

  /**
   * Fetch payment settings if not already done (e.g. navigated directly to cc screen)
   */
  useEffect(() => {
    if (!isObee || !isEmpty(paymentSettings.data)) {
      return;
    }

    const { date, availabilitySuggestion, guests, selectedExperiences } = booking;

    const areaId = availabilitySuggestion?.table?.areaId || booking?.areaId;

    if (!areaId) {
      // todo: confirm that if we should fetch payments for waitlist, theres a flow where joining the waitlist doesn't assign a table/area, areaId is required
      return;
    }

    const experienceCounts = selectedExperiences.filter(
      (experienceCount) => experienceCount?.count > 0,
    );

    // Experiences override payment settings
    if (!isEmpty(experienceCounts)) {
      // Find the first payment method on any of the experiences
      const paymentSettingId = experienceCounts.find(
        (experienceCount) => experienceCount?.experience?.paymentSettingId !== null,
      )?.experience?.paymentSettingId;

      if (!isEmpty(paymentSettingId)) {
        fetchPaymentSettingsById(paymentSettingId);
      }
    } else {
      fetchPaymentSettings({
        paymentSettings: {
          date,
          time: availabilitySuggestion?.time,
          size: guests,
          public: 1,
          areaId,
        },
        restId: restaurant.data.objectId,
      });
    }
  }, [
    booking?.availabilitySuggestion?.time,
    booking?.availabilitySuggestion?.table?.areaId,
    booking?.areaId,
    booking?.guests,
    booking?.date,
  ]);

  /**
   * Fetch booking terms if haven't already (e.g. navigated directly to this page)
   */
  useEffect(() => {
    // note: EatClub has the terms included in the restaurant object
    if (!isObee || !isEmpty(bookingTerms?.data?.terms_conditions)) {
      return;
    }

    // If we have custom terms, no need to fetch
    const customTerms = getExperienceTerms(booking);
    if (!isEmpty(customTerms)) {
      return;
    }

    const { date, availabilitySuggestion, guests } = booking;

    const areaId = availabilitySuggestion?.table?.areaId || booking?.areaId;

    const newVariables = {
      restId: restaurant.data.objectId,
      areaId,
      date,
      time: availabilitySuggestion?.time,
      size: guests,
    };

    if (deepEqual(newVariables, bookingTerms.lastUsedVariables)) {
      // the terms have been fetched
      return;
    }

    fetchBookingTerms(areaId, date, availabilitySuggestion?.time, guests);
  }, [
    booking?.availabilitySuggestion?.time,
    booking?.availabilitySuggestion?.table?.areaId,
    booking?.areaId,
    booking?.guests,
    booking?.date,
  ]);

  // TODO use same logic as bookings for paymentSettings
  const ccCapture = () => {
    if (isObee) {
      const showCC = !waitlist || (waitlist && waitlistCCRequired);
      // TODO support mandatory/optional payment settings
      return showCC && !isEmpty(paymentSettings?.data?.publishableKey);
    }

    return (
      (!waitlist && restaurant.data.ccCapture) ||
      (waitlist && restaurant.data.ccCapture && restaurant.data.ccWaitlistShow === 'mandatory')
    );
  };

  const hasPaymentMethod = !isEmpty(obeeBooking.data.paymentMethodName);

  const getOptimalArea = () => {
    const areaForBooking = restaurant.data.areas.find(
      (area) => area.id === booking.availabilitySuggestion?.table?.areaId ?? booking?.areaId,
    );

    if (areaForBooking) {
      return areaForBooking;
    }

    // NOTE: Find the optimal area for "all areas" waitlist
    const potentialAreas = restaurant.data.areas.filter(
      (area) => area.minGroup <= booking.guests && area.maxGroup >= booking.guests,
    );

    return potentialAreas.sort((a, b) => sortByProperty(a, b, 'priority'))[0];
  };

  const getAreaId = () => {
    if (booking.availabilitySuggestion?.table) {
      return booking.availabilitySuggestion?.table?.areaId;
    }

    if (booking.areaId !== -1) {
      return booking.areaId;
    }

    return getOptimalArea()?.id;
  };

  // Pay through payment provider
  const showCreditCardFields = isEditing(booking)
    ? ccCapture() && !hasPaymentMethod && (obeeBooking.data?.requiresCC || action === 'cc') // Editing and needs CC details
    : ccCapture(); // Not editing, depends on payment method fetched

  const experienceHasTermsBeforeAddons = booking?.selectedExperiences?.some(
    (experience) =>
      experience?.experience?.showTermsSeparately &&
      !isEmpty(experience?.experience?.termsAndConditions),
  );

  const hasImportantInformation =
    (isObee && bookingTerms.data.terms_exist) || (!isObee && !isEmpty(restaurant.data.terms));

  const showImportantInformation =
    !experienceHasTermsBeforeAddons &&
    ((isObee && bookingTerms.data.terms_exist) || (!isObee && !isEmpty(restaurant.data.terms)));

  const hasCCTerms = waitlist
    ? !isEmpty(restaurant.data.ccWaitlistPolicy)
    : !isEmpty(restaurant.data.ccPaymentPolicy);

  const hasTerms = hasImportantInformation || hasCCTerms;

  const experienceSummary = useMemo(() => {
    if (isEmpty(experiences)) {
      return [];
    }

    return booking?.selectedExperiences
      .filter((experience) => experience?.count > 0)
      .map((experience) => ({
        name: experience?.experience?.name,
        displayName: experience?.experience?.displayName,
        price: experience?.experience?.price,
        quantity: experience?.count,
        total: experience?.experience?.price * experience?.count,
      }));
  }, [experiences.data, booking?.selectedExperiences]);

  const addonCounts = Object.entries(booking.addons)
    .filter(([_addonId, addon]) => addon?.count > 0)
    .map(([_addonId, count]) => {
      // Note: we parse the int since object.entries can turn it into a string
      const addon = count?.addon;

      const total = addon?.includeInPayment ? addon?.price * count?.count : 0;
      const fullPrice =
        addon?.percentageOfFullPrice < 100
          ? safeDivision(1, addon?.percentageOfFullPrice) * total * 100
          : total;
      const payLaterAmount = addon?.includeInPayment
        ? fullPrice - total
        : addon?.price * count?.count;

      return {
        id: addon?.id,
        name: addon?.name,
        displayName: addon?.displayName,
        price: addon?.price,
        quantity: count?.count,
        total,
        compulsory: count?.compulsory,
        groupIndex: count?.groupIndex,
        addonGroupId: count?.addonGroupId,
        percentageOfFullPrice: addon?.percentageOfFullPrice, // e.g. 50% off
        fullPrice,
        payLaterAmount,
      };
    });

  const experiencesTotal = experienceSummary.reduce((totalPrice, experience) => {
    return totalPrice + experience?.total;
  }, 0);

  // The total dollar value of all vouchers combined. May be more than the purchase total
  const vouchersTotal = booking?.[bookingKeys.voucherReferences].reduce(
    (totalPrice, voucherReference) => {
      return totalPrice + voucherReference?.amount;
    },
    0,
  );

  // Calculate the total they are supposed to pay today
  const total =
    addonCounts.reduce((totalPrice, addonCount) => {
      return totalPrice + addonCount?.total;
    }, 0) + (experiencesTotal ?? 0);

  // Subtract the voucher amounts from the total
  const totalIncludingVouchers = Math.max(0, total - vouchersTotal);
  const voucherAmountAfterTotal = Math.max(0, vouchersTotal - total);

  // Calculate the total they will pay later
  const payLaterTotal = addonCounts.reduce((totalPrice, addonCount) => {
    return totalPrice + addonCount?.payLaterAmount;
  }, 0);

  // How much of the vouchers is allowed to be spent
  const maxAmountForVouchers = restaurant.data.useVouchersOnPayLater
    ? total + payLaterTotal
    : total;

  // If there is any voucher money still left, then use that in the pay later amount too
  const payLaterTotalIncludingVouchers = Math.max(
    0,
    restaurant.data.useVouchersOnPayLater ? payLaterTotal - voucherAmountAfterTotal : payLaterTotal,
  );

  // Whether or not this booking has any addons or experiences on it
  const hasAddonsOrExperiences = !isEmpty(addonCounts) || !isEmpty(experienceSummary);

  /**
   *   Get the effective credit card mode. It might be different to the setting on the venue.
   */
  const getPaymentMode = () => {
    if (isEmpty(paymentSettings?.data?.mode)) {
      return null;
    }

    if (waitlistCCRequired && booking?.waitlist) {
      return 'cc_store';
    }

    // If the payment method is set to full but there's no payment needed, then we store the credit card instead
    if (
      hasAddonsOrExperiences &&
      paymentSettings?.data?.mode === 'full' &&
      totalIncludingVouchers === 0
    ) {
      return 'cc_store';
    }

    return paymentSettings?.data?.mode;
  };
  const paymentMode = getPaymentMode();

  const updateAddonCount = (addonId, newValue) => {
    const newAddons = { ...booking?.addons };
    newAddons[addonId].count = newValue;

    updateHeldExperience(
      obeeBooking?.data?.id ?? booking?.heldBookingId,
      booking?.selectedExperiences,
      newAddons,
    );
  };

  /**
   * Work out how much to charge the guest.
   * TODO test this for ECB
   */
  const getDepositAmount = () => {
    // If full payment is required
    if (paymentMode === 'full') {
      // If using experiences or addons, use the total amount from that
      // TODO Should this get added to the total from the payment settings?
      if (totalIncludingVouchers > 0) {
        return totalIncludingVouchers;
      }

      // Otherwise use the "full amount" from the settings
      if (paymentSettings.data.fullOption === 'per_person') {
        return paymentSettings.data.fullAmount * booking.guests;
      }

      return paymentSettings.data.fullAmount;
    }

    // If they take a deposit
    if (paymentMode === 'deposit') {
      if (paymentSettings.data.depositOption === 'per_person') {
        return paymentSettings.data.depositAmount * booking.guests;
      }

      return paymentSettings.data.depositAmount;
    }

    return 0;
  };

  const confirmBooking = async () => {
    onKillTimer();
    setConfirmingBooking(true);

    const areaId = getAreaId();
    const area = getOptimalArea(areaId);
    let tableNumber = booking.availabilitySuggestion?.table?.tableNumber;
    let tableId = booking.availabilitySuggestion?.table?.id;

    const duration = getBookingDuration(
      area,
      restaurant.data.areas,
      booking.availabilitySuggestion.time,
    );

    let { paymentMethodId, paymentMethodName, setupIntentId } = booking;

    try {
      if (booking.editBooking && action !== 'cc') {
        const { available, tables } = await bookingUpdateCheckAction(
          obeeBooking.data.id,
          areaId,
          booking.date,
          booking.availabilitySuggestion?.time,
          booking.guests,
          tableNumber,
          obeeBooking.data.duration,
        );

        if (!available) {
          if (isEmpty(tables)) {
            // No other tables available
            addMessage(
              `CONFIRM_BOOKING_${new Date().getTime()}`,
              `Sorry, the booking on ${dateFormatted} for ${booking.guests} guests at ${timeFormatted} is currently unavailable.`,
              'error',
              'Booking update unavailable',
              'confirmBooking',
              {
                bookingId: obeeBooking.data.id,
                areaId,
                date: booking.date,
                time: booking.availabilitySuggestion?.time,
                size: booking.guests,
                tableNumber,
                duration,
              },
            );

            setConfirmingBooking(false);
            return;
          }
          // Another table is available
          tableNumber = tables?.[0]?.table?.table_number;
          tableId = tables?.[0]?.table?.id;
        }
      }

      if (showCreditCardFields) {
        const paymentResponse = await paymentRef?.current?.submit();

        paymentMethodId = paymentResponse?.token;
        paymentMethodName = paymentResponse?.cardNumber;
        setupIntentId = paymentResponse?.setupIntentId;

        if (!paymentResponse?.valid) {
          devLog('info', 'paymentResponse', 'invalid response...');
          setConfirmingBooking(false);
          addMessage(
            `CONFIRM_BOOKING_${new Date().getTime()}`,
            `Failed to submit credit card details`,
            'error',
            `Failed to submit credit card details`,
            'confirmBooking',
            { cardName, ...booking },
          );
          return;
        }

        batchUpdateBooking({
          [bookingKeys.cardName]: paymentResponse?.cardName,
          [bookingKeys.paymentMethodId]: paymentMethodId,
          [bookingKeys.paymentMethodName]: paymentMethodName,
          [bookingKeys.setupIntentId]: setupIntentId,
          [bookingKeys.ccMode]: paymentMode,
        });
      }

      // Send strings to represent the dietary tags
      const dietaryOptions = [
        ...dietaryTags?.data.map((dietaryTag) => ({
          label: dietaryTag.dietaryTag,
          value: dietaryTag.id,
        })),
        {
          value: 'other',
          label: 'Other',
        },
      ];

      const dietaryRequirements = booking.dietaryRequirements?.map((guest) => ({
        ...guest,
        dietaries: (guest?.dietaries || [])
          .filter((allergy) => allergy !== 'other')
          .map((allergy) => getLabelForValue(allergy, dietaryOptions)),
      }));

      const vouchersWithTotals = getVouchersWithTotals(
        booking.voucherReferences,
        maxAmountForVouchers,
      );

      const voucherRedemptions = vouchersWithTotals.map((voucher) => ({
        voucherRef: voucher.voucher,
        amount: voucher.usedAmount,
        // redemptionType: voucher.remainingAmount === 0 ? 'full' : 'partial',
      }));

      const selectedExperiences = booking?.selectedExperiences ?? [];
      const prefilledMessage = selectedExperiences.map((exp) => exp?.experience?.name)?.join(', ');

      const OBBooking = {
        areaId: waitlist ? null : areaId,
        date: booking.date,
        time: booking.availabilitySuggestion?.time,
        size: booking.guests,
        guests: {
          firstName: booking.firstName,
          lastName: booking.lastName,
          email: booking.email,
          mobile: booking.mobile,
          smsPromo: subscribe,
          emailPromo: subscribe,
          mobilePrefix: booking.mobile ? booking?.mobilePrefix ?? '+61' : null,
        },
        name: `${booking.firstName} ${booking.lastName}`,
        customerComments: booking.specialRequirements,
        tableNumber: tableNumber || null,
        tableId: tableId || null,
        highchairs: booking.highchairs,
        kids: booking.kids,
        waitlist,
        ...(showCreditCardFields && {
          paymentMethodId,
          paymentMethodName,
          setupIntentId,
        }),
        ...(showCreditCardFields &&
          isObee && {
            cardToken: paymentMethodId,
          }),
        // Only send amount if using experiences or addons
        ...(showCreditCardFields &&
          isObee &&
          paymentMode === 'full' &&
          hasAddonsOrExperiences && {
            amount: dollarsToCents(totalIncludingVouchers).toFixed(2),
          }),
        ...(isObee && {
          paymentSettingId: paymentSettings?.data?.id || null,
          ccMode: paymentMode,
        }),
        duration,
        ...(booking.heldBookingId && {
          heldBookingId: booking.heldBookingId,
        }),
        dietaryRequirements: emptyToNull(dietaryRequirements),
        voucherRedemptions: emptyToNull(voucherRedemptions),
        postcode: emptyToNull(booking?.postcode),
        ...(waitlist &&
          selectedExperiences.length > 0 && {
            restaurantComments: `Guest would like ${prefilledMessage}`,
          }),
        // voucherReferences: emptyToNull(booking?.voucherReferences),
      };

      if (booking.editBooking) {
        updateObeeBooking(obeeBooking.data.id, OBBooking);
      } else {
        createObeeBooking(OBBooking);
      }
    } catch (error) {
      addMessage(
        `CONFIRM_BOOKING_${new Date().getTime()}`,
        `${error}`,
        'error',
        `${error}`,
        'confirmBooking',
        { cardName, ...booking },
      );

      setConfirmingBooking(false);
    }
  };

  useEffect(() => {
    if (confirmingBooking && obeeBooking.success) {
      setConfirmingBooking(false);

      updateBookingStep(bookingSteps.confirm);
    }
  }, [obeeBooking.success]);

  useEffect(() => {
    if (confirmingBooking && obeeBooking.error) {
      const error =
        obeeBooking.error ??
        "Sorry, we're unable to confirm the booking right now. Please try again later.";
      addMessage(`CONFIRM_BOOKING_${new Date().getTime()}`, error, 'error');

      setConfirmingBooking(false);
    }
  }, [obeeBooking.error]);

  const getTerms = () => {
    // If the experience has terms, use those instead
    const customTerms = getExperienceTerms(booking);
    const termsToUse = customTerms?.termsConditions ?? bookingTerms.data.terms_conditions;

    if (isObee) {
      return (
        <Typography style={{ whiteSpace: 'pre-line' }}>
          <div dangerouslySetInnerHTML={{ __html: termsToUse }} />
        </Typography>
      );
    }
    try {
      const contentState = convertFromRaw(JSON.parse(restaurant.data.terms));
      const editorState = EditorState.createWithContent(contentState);

      return <Editor editorState={editorState} readOnly onChange={() => {}} />;
    } catch {
      return null;
    }
  };
  const getDepositMessage = () => {
    if (paymentMode === 'deposit') {
      return `You will be charged the deposit amount of ${formatCurrency(getDepositAmount())}`;
    }

    if (paymentMode === 'cc_store') {
      return 'Please provide card details below';
    }

    if (paymentMode === 'full') {
      return 'Please provide payment details below';
    }

    return null;
  };

  const hideShowPaymentPolicy = safeParse(restaurant.data?.bookingWidgetSettings)?.themeSettings
    ?.themeDetails?.hideShowPaymentPolicy;

  const hideZeroDollarPrice = safeParse(restaurant?.data?.bookingWidgetSettings)?.widgetSettings
    ?.settingsDetails?.hideZeroDollarPrice;

  const showTotal = useMemo(() => {
    if (
      hideZeroDollarPrice === true &&
      (isEmpty(totalIncludingVouchers) || totalIncludingVouchers === 0)
    ) {
      return false;
    }

    return true;
  }, [totalIncludingVouchers]);

  const addonGroupList = useMemo(
    () => getAddonGroupsForBooking(booking, addonGroups)?.all,
    [addonGroups, booking],
  );

  const hideEditBtn = action === 'cc';

  return (
    <PageLayout>
      <WidgetLayout showDetails showBackButton={!obeeBooking.data?.requiresCC && action !== 'cc'}>
        <Spacer direction='vertical' gap={32}>
          <>
            <Spacer direction='vertical' gap='m'>
              <Typography variant='h2'>
                {obeeBooking.data?.requiresCC
                  ? `Provide your credit card to secure your booking`
                  : `Review ${showCreditCardFields ? ' and payment details' : ''}`}
              </Typography>

              {showImportantInformation && (
                <Box
                  style={{
                    fontFamily: theme.baseTypography.availabilityFontFamily,
                    whiteSpace: 'pre-line',
                    lineHeight: '1.4em',
                    maxHeight: '240px',
                    overflowY: 'auto',
                  }}
                >
                  {getTerms()}
                </Box>
              )}
            </Spacer>
            <Divider color={theme.colors.dividerColor} />
          </>

          {(!isEmpty(experienceSummary) || !isEmpty(addonCounts)) && !booking?.waitlist && (
            <>
              <Spacer direction='vertical' gap={4}>
                <Spacer style={{ justifyContent: 'space-between' }}>
                  <Typography variant='h3'>Summary</Typography>
                  {/* <Button */}
                  {/*   type='text' */}
                  {/*   variant='text' */}
                  {/*   style={{ marginRight: '-16px' }} */}
                  {/*   onClick={() => { */}
                  {/*     // TODO go to exact addon group */}
                  {/*     updateBookingStep(bookingSteps.addons); */}
                  {/*   }} */}
                  {/* > */}
                  {/*   Edit */}
                  {/* </Button> */}
                </Spacer>

                {/* <Divider color={theme.colors.dividerColor} /> */}

                <Spacer gap={6} direction='vertical'>
                  <table className={classes.summaryTable}>
                    <tbody>
                      {!isEmpty(experienceSummary) &&
                        experienceSummary.map((experience) => (
                          <tr key={`${experience?.id}-${experience?.name}`}>
                            <td>{experience?.displayName || experience?.name}</td>
                            <td>{experience?.quantity}</td>
                            <td
                              style={{
                                textAlign: 'right',
                                width: '120px',
                                fontFamily: theme.baseTypography.numberFontFamily,
                              }}
                            >
                              {experience?.total > 0 && formatCurrency(experience?.total)}
                            </td>
                          </tr>
                        ))}
                      {addonCounts.map((addonCount) => (
                        <tr key={addonCount?.name}>
                          <td>
                            <Spacer gap={2} direction='vertical'>
                              <Box>{addonCount?.name}</Box>
                              {addonCount?.percentageOfFullPrice < 100 ? (
                                <Box style={{ fontSize: '14px', color: theme.colors.smoke }}>
                                  {formatCurrency(addonCount?.payLaterAmount)}{' '}
                                  {restaurant.data.percentagePaidText}
                                </Box>
                              ) : (
                                <>
                                  {addonCount?.payLaterAmount > 0 && (
                                    <Box style={{ fontSize: '14px', color: theme.colors.smoke }}>
                                      {formatCurrency(addonCount?.payLaterAmount)}{' '}
                                      {restaurant.data.percentagePaidText}
                                    </Box>
                                  )}
                                </>
                              )}

                              <Spacer>
                                {!hideEditBtn && (
                                  <ECButton
                                    type='text'
                                    style={{
                                      button: { textDecoration: 'underline', fontSize: '14px' },
                                    }}
                                    onClick={() => {
                                      // Need to do a bit of logic to figure out whether we go to the "before details" page or not
                                      const allAddonGroups = getAddonGroupsForBooking(
                                        booking,
                                        addonGroups,
                                      )?.all;
                                      const addonGroup = findObjectByProperty(
                                        allAddonGroups,
                                        addonCount?.addonGroupId,
                                        'id',
                                      );

                                      updateBookingStep(
                                        addonGroup?.showBeforeDetails
                                          ? bookingSteps.addonsBeforeDetails
                                          : bookingSteps.addons,
                                        addonCount?.groupIndex,
                                      );
                                    }}
                                  >
                                    Edit
                                  </ECButton>
                                )}

                                {/* {!addonCount.compulsory && ( */}
                                {/*   <ECButton */}
                                {/*     type='text' */}
                                {/*     style={{ */}
                                {/*       button: { textDecoration: 'underline', fontSize: '14px' }, */}
                                {/*     }} */}
                                {/*     onClick={() => removeAddon(addonCount.id)} */}
                                {/*   > */}
                                {/*     Remove */}
                                {/*   </ECButton> */}
                                {/* )} */}
                              </Spacer>
                            </Spacer>
                          </td>
                          <td>{addonCount?.quantity}</td>
                          {/* <td> */}
                          {/*   <GuestSelect */}
                          {/*     variant='big' */}
                          {/*     onDecrease={() => {}} */}
                          {/*     onIncrease={() => {}} */}
                          {/*     min={0} */}
                          {/*     max={10} */}
                          {/*     value={addonCount?.quantity} */}
                          {/*     onSetValue={(newValue) => updateAddonCount(addonCount?.id, newValue)} */}
                          {/*     unit='' */}
                          {/*   /> */}
                          {/* </td> */}
                          <td
                            style={{
                              textAlign: 'right',
                              width: '120px',
                              fontFamily: theme.baseTypography.numberFontFamily,
                            }}
                          >
                            {hideZeroDollarPrice === true &&
                            (isEmpty(addonCount?.total) || addonCount?.total === 0)
                              ? null
                              : formatCurrency(addonCount?.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {!booking?.waitlist && addonGroupList?.length > 0 && (
                    <ECButton
                      type='text'
                      style={{
                        button: {
                          textDecoration: 'underline',
                          fontSize: '14px',
                          paddingBottom: '28px',
                        },
                      }}
                      onClick={() => {
                        updateBookingStep(bookingSteps.addons, 0);

                        const allAddonGroups = getAddonGroupsForBooking(booking, addonGroups);
                        const hasBeforeDetails = !isEmpty(allAddonGroups?.beforeDetails);

                        updateBookingStep(
                          hasBeforeDetails ? bookingSteps.addonsBeforeDetails : bookingSteps.addons,
                          0,
                        );
                      }}
                    >
                      Add more
                    </ECButton>
                  )}

                  <VoucherReferences bookingTotal={maxAmountForVouchers} />

                  <Spacer direction='vertical' gap={15}>
                    {showTotal && <Divider color={theme.colors.dividerColor} />}
                    {showTotal && (
                      <Spacer
                        style={{
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography>
                          {showCreditCardFields && paymentMode === 'full'
                            ? restaurant.data.paymentDueTodayText
                            : 'Total'}
                        </Typography>
                        <Typography
                          style={{
                            fontFamily: theme.baseTypography.numberFontFamily,
                          }}
                        >
                          {formatCurrency(totalIncludingVouchers)}
                        </Typography>
                      </Spacer>
                    )}

                    {payLaterTotalIncludingVouchers > 0 && (
                      <Spacer
                        style={{
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography>{restaurant.data.paymentDueLaterText}</Typography>
                        <Typography
                          style={{
                            fontFamily: theme.baseTypography.numberFontFamily,
                          }}
                        >
                          {formatCurrency(payLaterTotalIncludingVouchers)}
                        </Typography>
                      </Spacer>
                    )}

                    <Divider color={theme.colors.dividerColor} />
                  </Spacer>
                </Spacer>
              </Spacer>
            </>
          )}

          {showCreditCardFields && (
            <>
              <Spacer direction='vertical' gap={24}>
                <Spacer direction='vertical' gap={12}>
                  <Spacer style={{ justifyContent: 'space-between' }}>
                    <Typography variant='h3'>
                      Payment details
                      {/* {paymentSettings.data.title ?? 'Payment details'} */}
                      {/* Payment details{paymentSettings.data.title && `: ${paymentSettings.data.title}`} */}
                    </Typography>

                    {paymentSettings.data.policyText ? (
                      <Button
                        onClick={() => setShowPaymentPolicyModal(true)}
                        variant='outlined'
                        style={{
                          fontWeight: theme.fontWeights.regular,
                          width: 'fit-content',
                          fontSize: '13px',
                        }}
                        size='small'
                      >
                        Show policy
                      </Button>
                    ) : (
                      <Box />
                    )}
                  </Spacer>

                  {waitlist ? (
                    <Box>
                      {!isEmpty(restaurant.data.ccWaitlistPolicy) && (
                        <Typography style={{ lineHeight: '1.4em' }}>
                          {restaurant.data.ccWaitlistPolicy}
                        </Typography>
                      )}

                      {waitlist && waitlistCCRequired && !isEmpty(waitlistCCShortDesc) && (
                        <Typography style={{ lineHeight: '1.4em' }}>
                          {waitlistCCShortDesc}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      {!isEmpty(
                        restaurant.data.ccPaymentPolicy || paymentSettings.data.shortDescription,
                      ) &&
                        !hideShowPaymentPolicy && (
                          <Typography
                            style={{
                              lineHeight: '1.4em',
                              whiteSpace: 'pre-wrap',
                              margin: '10px 0',
                            }}
                          >
                            <span
                              dangerouslySetInnerHTML={{
                                __html:
                                  restaurant.data.ccPaymentPolicy ||
                                  paymentSettings.data.shortDescription,
                              }}
                            />
                          </Typography>
                        )}

                      <Spacer style={{ justifyContent: 'space-between' }}>
                        {/* Show payment info */}
                        <Typography style={{ lineHeight: '1.4em', whiteSpace: 'pre-wrap' }}>
                          {getDepositMessage()}
                        </Typography>

                        <Spacer
                          gap={0}
                          style={{ imageRendering: 'smooth', imageResolution: 'from-image' }}
                        >
                          <MasterCardSVG />
                          <VisaSVG />
                          <AmexSVG style={{ paddingLeft: '4px' }} />
                        </Spacer>
                      </Spacer>
                    </Box>
                  )}
                </Spacer>

                <CardFields
                  paymentRef={paymentRef}
                  phone={booking?.mobile}
                  email={booking?.email}
                  paymentMethodName={booking?.paymentMethodName}
                  confirmingBooking={confirmingBooking}
                  booking={booking}
                />
              </Spacer>

              {/* <Divider color={theme.colors.outlineColour} /> */}

              {showPaymentPolicyModal && (
                <PaymentPolicyModal
                  open={showPaymentPolicyModal}
                  onClose={() => setShowPaymentPolicyModal(false)}
                  text={
                    booking?.waitlist &&
                    waitlistCCRequired &&
                    !isEmpty(waitlistCCTermsAndConditions)
                      ? waitlistCCTermsAndConditions
                      : paymentSettings.data.policyText
                  }
                />
              )}
            </>
          )}

          <Spacer direction='vertical' gap={20}>
            {canSubscribe && (
              <Spacer>
                <Checkbox
                  label={
                    <Typography variant='small'>
                      {!isEmpty(restaurant?.data?.marketingOptInMessage)
                        ? restaurant?.data?.marketingOptInMessage
                        : `I would like to receive marketing updates from ${restaurant.data.name}`}
                    </Typography>
                  }
                  color={theme.colors.charcoal}
                  onChange={setSubscribe}
                  selected={subscribe}
                  style={{ checkboxBox: { backgroundColor: 'transparent' } }}
                />
              </Spacer>
            )}

            <Box>
              <Button
                color='primary'
                fullWidth={isMobile}
                loading={confirmingBooking}
                onClick={() => {
                  trackCompletion();
                  confirmBooking();
                }}
                size='large'
              >
                {/* eslint-disable-next-line no-nested-ternary */}
                {booking.editBooking
                  ? 'Update booking'
                  : waitlist
                  ? 'Join the waitlist'
                  : showCreditCardFields && paymentMode !== 'cc_store'
                  ? 'Pay and submit booking'
                  : 'Submit booking'}
              </Button>
            </Box>

            {showCreditCardFields && hasTerms && (
              <Typography variant='caption' style={{ color: theme.colors.secondaryTextColour }}>
                By clicking submit you agree to the venue&apos;s terms of service.
              </Typography>
            )}
          </Spacer>
        </Spacer>
      </WidgetLayout>
    </PageLayout>
  );
};

Review.defaultProps = {
  action: null,
};

Review.propTypes = {
  addMessage: PropTypes.func.isRequired,
  batchUpdateBooking: PropTypes.func.isRequired,
  booking: bookingPropTypes.isRequired,
  bookingTerms: bookingTermsPropTypes.isRequired,
  createObeeBooking: PropTypes.func.isRequired,
  obeeBooking: obeeBookingPropTypes.isRequired,
  restaurant: restaurantPropTypes.isRequired,
  updateBookingStep: PropTypes.func.isRequired,
  updateObeeBooking: PropTypes.func.isRequired,
  paymentSettings: paymentSettingsPropTypes.isRequired,
  fetchPaymentSettings: PropTypes.func.isRequired,
  fetchPaymentSettingsById: PropTypes.func.isRequired,
  fetchBookingTerms: PropTypes.func.isRequired,
  experiences: experiencesPropTypes.isRequired,
  addonGroups: addonGroupsPropTypes.isRequired,
  updateHeldExperience: PropTypes.func.isRequired,
  dietaryTags: dietaryTagsPropTypes.isRequired,
  action: PropTypes.string,
  onKillTimer: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  booking: state.booking,
  bookingTerms: state.bookingTerms,
  obeeBooking: state.obeeBooking,
  restaurant: state.restaurant,
  paymentSettings: state.paymentSettings,
  experiences: state.experiences,
  addonGroups: state.addonGroups,
  dietaryTags: state.dietaryTags,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      addMessage: addMessageAction,
      batchUpdateBooking: batchUpdateBookingAction,
      createObeeBooking: createObeeBookingAction,
      updateBookingStep: updateBookingStepAction,
      updateObeeBooking: updateObeeBookingAction,
      fetchPaymentSettings: fetchPaymentSettingsAction,
      fetchPaymentSettingsById: fetchPaymentSettingsByIdAction,
      fetchBookingTerms: fetchBookingTermsAction,
      removeAddon: removeAddonAction,
      updateHeldExperience: updateHeldExperienceAction,
      updateBooking: updateBookingAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(Review);
